import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FiEye, FiEyeOff, FiMoon, FiSun } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext.js";
import ForgotPasswordModal from "./ForgotPasswordModal";
import ResetPasswordForm from "./ResetPasswordForm.js";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/Praxiti-Logo.png";

const AuthForm = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetFlowActive, setResetFlowActive] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    username: "",
    password: "",
    email: "",
    address: "",
    phoneNumber: "",
    tenantId: "",
    applicationCode: "",
    remember: false,
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin");
    }
  }, [isAuthenticated, navigate]);

  // Password regex
  const passwordRegex = {
    length: /.{8,}/,
    uppercase: /[A-Z]/,
    lowercase: /[a-z]/,
    number: /[0-9]/,
    specialChar: /[!@#$%^&*()_+\-=[\]{};:'"\\|,.<>/?]/,
  };

  useEffect(() => {
    document.body.setAttribute("data-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      fullName: "",
      username: "",
      password: "",
      email: "",
      address: "",
      phoneNumber: "",
      tenantId: "",
      applicationCode: "",
      remember: false,
    });
    setErrors({});
    setShowPassword(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validatePassword = (password) => {
    // Skip validation for login
    if (isLogin) return true;

    const strengthChecks = {
      length: passwordRegex.length.test(password),
      uppercase: passwordRegex.uppercase.test(password),
      lowercase: passwordRegex.lowercase.test(password),
      number: passwordRegex.number.test(password),
      specialChar: passwordRegex.specialChar.test(password),
    };
    const failedChecks = Object.keys(strengthChecks).filter(
      (key) => !strengthChecks[key]
    );

    if (failedChecks.length > 0) {
      let msg = "Password must contain:";
      if (!strengthChecks.length) msg += " at least 8 characters,";
      if (!strengthChecks.uppercase) msg += " one uppercase,";
      if (!strengthChecks.lowercase) msg += " one lowercase,";
      if (!strengthChecks.number) msg += " one number,";
      if (!strengthChecks.specialChar) msg += " one special character,";
      msg = msg.slice(0, -1) + ".";
      setErrors((prev) => ({ ...prev, password: msg }));
      return false;
    } else {
      setErrors((prev) => {
        const { password, ...rest } = prev;
        return rest;
      });
      return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    let formIsValid = true;

    // Username validation
    if (!formData.username.trim()) {
      setErrors((prev) => ({ ...prev, username: "Username is required" }));
      formIsValid = false;
    }

    // Password validation
    if (isLogin) {
      if (!formData.password) {
        setErrors((prev) => ({ ...prev, password: "Password is required" }));
        formIsValid = false;
      }
    } else if (!validatePassword(formData.password)) {
      formIsValid = false;
    }

    // Name validation
    if (!isLogin && !formData.fullName.trim()) {
      setErrors((prev) => ({ ...prev, fullName: "Full name is required" }));
      formIsValid = false;
    }

    // Email validation
    if (!isLogin && !formData.email.trim()) {
      setErrors((prev) => ({ ...prev, email: "Email is required" }));
      formIsValid = false;
    } else if (!isLogin && !/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors((prev) => ({ ...prev, email: "Invalid email format" }));
      formIsValid = false;
    }

    // Address validation
    if (!isLogin && !formData.address.trim()) {
      setErrors((prev) => ({ ...prev, address: "Address is required" }));
      formIsValid = false;
    }

    // Phone number validation
    if (!isLogin && !formData.phoneNumber.trim()) {
      setErrors((prev) => ({
        ...prev,
        phoneNumber: "Phone number is required",
      }));
      formIsValid = false;
    }

    // Tenant ID validation
    if (!isLogin && !formData.tenantId.trim()) {
      setErrors((prev) => ({ ...prev, tenantId: "Tenant ID is required" }));
      formIsValid = false;
    }

    if (!isLogin && !formData.applicationCode.trim()) {
      setErrors((prev) => ({
        ...prev,
        applicationCode: "Application Code is required",
      }));
      formIsValid = false;
    }

    if (!formIsValid) {
      toast.error("Please correct the errors in the form.");
      setIsSubmitting(false);
      return;
    }

    if (isLogin) {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_AUTH_BASE_URL}/api/auth/login`,
          {
            username: formData.username,
            password: formData.password,
          }
        );

        if (res?.data?.success && res?.data?.data) {
          login(res.data);
          navigate("/admin");
          toast.success("Logged in successfully!");
        } else {
          toast.error(res.data.message || "Login failed!");
        }
      } catch (error) {
        toast.error(error.response?.data?.message);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_AUTH_BASE_URL}/api/auth/register`,
          {
            username: formData.username,
            password: formData.password,
            fullName: formData.fullName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            address: formData.address,
            tenantId: formData.tenantId,
            applicationCode: formData.applicationCode,
          }
        );

        if (res?.data?.success) {
          toast.success("Registered successfully!");
          setIsLogin(true);
          setFormData({
            fullName: "",
            username: "",
            password: "",
            email: "",
            address: "",
            phoneNumber: "",
            tenantId: "",
            applicationCode: "",
            remember: false,
          });
        } else {
          toast.error(res.data.message || "Registration failed!");
        }
      } catch (error) {
        toast.error(error.response?.data?.message);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setForgotOpen(true);
  };

  const handleResetRequestSuccess = (username) => {
    setForgotOpen(false);
    setResetFlowActive(true);
    setResetUsername(username);
    toast.info(`A password reset link has been sent to ${username}.`);
  };

  if (resetFlowActive) {
    return (
      <ResetPasswordForm
        username={resetUsername}
        onBackToLogin={() => setResetFlowActive(false)}
      />
    );
  }

  return (
    <div className="auth-container">
      <div className="theme-toggle" onClick={() => setDarkMode(!darkMode)}>
        {darkMode ? <FiSun /> : <FiMoon />}
      </div>

      <ForgotPasswordModal
        isOpen={forgotOpen}
        onClose={() => setForgotOpen(false)}
        onResetRequestSuccess={handleResetRequestSuccess}
      />

      <div className="form-box">
        <div className="form-header">
          
          <h2>{isLogin ? "Login to Praxiti" : "Register at Praxiti"}</h2>
        </div>

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className={errors.fullName ? "error" : ""}
                  required
                />
                {errors.fullName && (
                  <p className="error-message">{errors.fullName}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@gmail.com"
                  className={errors.email ? "error" : ""}
                  required
                />
                {errors.email && (
                  <p className="error-message">{errors.email}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="91 9874561230"
                  className={errors.phoneNumber ? "error" : ""}
                  required
                />
                {errors.phoneNumber && (
                  <p className="error-message">{errors.phoneNumber}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter your address"
                  className={errors.address ? "error" : ""}
                  required
                />
                {errors.address && (
                  <p className="error-message">{errors.address}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="tenantId">Tenant ID</label>
                <input
                  type="text"
                  id="tenantId"
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleChange}
                  placeholder="Enter tenant ID"
                  className={errors.tenantId ? "error" : ""}
                  required
                />
                {errors.tenantId && (
                  <p className="error-message">{errors.tenantId}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="applicationCode">Application Code</label>
                <input
                  type="text"
                  id="applicationCode"
                  name="applicationCode"
                  value={formData.applicationCode}
                  onChange={handleChange}
                  placeholder="Enter your application code"
                  className={errors.applicationCode ? "error" : ""}
                  required
                />
                {errors.applicationCode && (
                  <p className="error-message">{errors.applicationCode}</p>
                )}
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter your username"
              className={errors.username ? "error" : ""}
              required
            />
            {errors.username && (
              <p className="error-message">{errors.username}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                autoComplete="off"
                className={errors.password ? "error" : ""}
                required
              />
              <span
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>
            {errors.password && (
              <p className="error-message">{errors.password}</p>
            )}
            {!isLogin && formData.password && (
              <div className="password-strength">
                <p>Password must contain:</p>
                <ul>
                  <li
                    className={
                      passwordRegex.length.test(formData.password)
                        ? "valid"
                        : ""
                    }
                  >
                    At least 8 characters
                  </li>
                  <li
                    className={
                      passwordRegex.uppercase.test(formData.password)
                        ? "valid"
                        : ""
                    }
                  >
                    One uppercase letter
                  </li>
                  <li
                    className={
                      passwordRegex.lowercase.test(formData.password)
                        ? "valid"
                        : ""
                    }
                  >
                    One lowercase letter
                  </li>
                  <li
                    className={
                      passwordRegex.number.test(formData.password)
                        ? "valid"
                        : ""
                    }
                  >
                    One number
                  </li>
                  <li
                    className={
                      passwordRegex.specialChar.test(formData.password)
                        ? "valid"
                        : ""
                    }
                  >
                    One special character
                  </li>
                </ul>
              </div>
            )}
          </div>

          {isLogin && (
            <>
              <div className="forgot-password">
                <a href="/" onClick={handleForgotPasswordClick}>
                  Forgot Password?
                </a>
              </div>
              <label className="remember-me">
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                />
                Remember me
              </label>
            </>
          )}

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Processing..." : isLogin ? "Login" : "Register"}
          </button>
        </form>

        <div className="form-toggle">
          <p onClick={toggleMode}>
            {isLogin ? "New here? Register" : "Already a user? Login"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
